/**
 * The eight evening motifs, drawn in the manner of Athenian red-figure pottery:
 * the figure is left in the colour of the clay and the ground around it is
 * painted black, with the detail inside the figure drawn in thin dark lines.
 *
 * Each motif is the object that evening turns on, taken from the story itself.
 * They are drawn to sit inside Tondo.jsx, on a 120 by 120 field with the centre
 * at 60,60 and about 38 units of room on every side.
 *
 * These are vectors drawn for the app, not copies of any surviving vase.
 */
import React from 'react';

// The courtyard palette (tailwind.config.js), spelt out because SVG presentation
// attributes cannot read Tailwind classes.
export const CLAY = '#C2703E'; // courtyard-terracotta: the reserved clay
export const CLAY_LIGHT = '#D4895A'; // courtyard-terracotta-light
export const GLAZE = '#2A2622'; // courtyard-stone-dark: the black glaze
export const GILT = '#D4A24E'; // courtyard-gold: added colour, used sparingly

const line = { fill: 'none', stroke: GLAZE, strokeWidth: 1.4, strokeLinecap: 'round', strokeLinejoin: 'round' };
const clayLine = { fill: 'none', stroke: CLAY, strokeLinecap: 'round', strokeLinejoin: 'round' };

/** A plain clay drinking cup with two small handles, standing on a short foot. */
const Cup = ({ x = 0, y = 0, scale = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <path d="M-12 -9 C-17 -10 -18 -2 -10 -3" {...clayLine} strokeWidth={2.4} />
    <path d="M12 -9 C17 -10 18 -2 10 -3" {...clayLine} strokeWidth={2.4} />
    <path d="M-13 -12 H13 C13 -2 7 4 2.4 5 L2.4 10 L8 13 H-8 L-2.4 10 L-2.4 5 C-7 4 -13 -2 -13 -12 Z" fill={CLAY} />
    <path d="M-11.6 -8 H11.6" {...line} strokeWidth={1.2} />
    <path d="M-10 -4.6 H10" {...line} strokeWidth={0.9} />
  </g>
);

/** Evening 1: the wine jug, the first cup, and what went on the stone. */
export const JugAndCup = () => (
  <g>
    {/* the jug, its handle rising behind the lip */}
    <path d="M35 34 C24 32 21 50 30 58" {...clayLine} strokeWidth={3.2} />
    <path
      d="M39 44 C39 50 27 54 27 68 C27 80 35 86 38 88 L35 92 H55 L52 88 C55 86 63 80 63 68 C63 54 51 50 51 44 L57 33 C51 37 43 37 34 32 Z"
      fill={CLAY}
    />
    <path d="M39.6 45.5 H50.4" {...line} />
    <path d="M28.2 62 C38 65.5 52 65.5 61.8 62" {...line} />
    <path d="M27.6 70 C38 73.5 52 73.5 62.4 70" {...line} />
    <path d="M36.4 89.6 H53.6" {...line} strokeWidth={1.1} />
    {/* the wine that missed */}
    <circle cx={63} cy={41} r={1.7} fill={GILT} />
    <circle cx={68} cy={49} r={2} fill={GILT} />
    <circle cx={73} cy={57} r={2.3} fill={GILT} />
    <Cup x={82} y={76} scale={1.05} />
  </g>
);

/** Evening 2: the lamp on the low table, and the moth that had your attention. */
export const LampAndMoth = () => (
  <g>
    {/* lamp: a low round body, a long nozzle, a loop to carry it by */}
    <path d="M36 72 C24 68 24 84 37 80" {...clayLine} strokeWidth={3} />
    <path
      d="M34 77 C34 67 46 63 58 63 C66 63 72 66 78 69 L91 69 C95 69 95 76 91 76 L80 77 C74 84 62 86 52 86 C42 86 34 84 34 77 Z"
      fill={CLAY}
    />
    <path d="M44 86 H62 L60 90 H46 Z" fill={CLAY} />
    <ellipse cx={54} cy={66.4} rx={6.5} ry={1.7} fill={GLAZE} />
    <path d="M36.5 76 C46 79.5 66 79.5 78 73.5" {...line} />
    <path d="M45.4 87.8 H60.6" {...line} strokeWidth={1.1} />
    {/* flame */}
    <path d="M91 68 C86 61 92 57 91 48 C97 55 99 62 95 68 Z" fill={GILT} />
    {/* moth */}
    <g transform="rotate(-18 60 38)">
      <path d="M59 36 C49 26 41 31 45 41 C48 47 56 45 59 40 Z" fill={CLAY} />
      <path d="M61 36 C71 26 79 31 75 41 C72 47 64 45 61 40 Z" fill={CLAY} />
      <path d="M59.2 41 C53 45 51 51 56 52 C59 51 60 46 60 42 Z" fill={CLAY_LIGHT} />
      <path d="M60.8 41 C67 45 69 51 64 52 C61 51 60 46 60 42 Z" fill={CLAY_LIGHT} />
      <ellipse cx={60} cy={41} rx={1.7} ry={7} fill={GILT} />
      <circle cx={50.5} cy={36.5} r={1.5} fill={GLAZE} />
      <circle cx={69.5} cy={36.5} r={1.5} fill={GLAZE} />
      <path d="M59.4 34 C58 30 56 28 54 27.5" {...clayLine} stroke={GILT} strokeWidth={0.9} />
      <path d="M60.6 34 C62 30 64 28 66 27.5" {...clayLine} stroke={GILT} strokeWidth={0.9} />
    </g>
  </g>
);

const GRAPES = [
  [45, 45], [55, 45], [65, 45], [75, 45],
  [50, 54], [60, 54], [70, 54],
  [55, 63], [65, 63],
  [60, 72],
];

/** Evening 3: the bunch Theron counted from, and the three that were left. */
export const Grapes = () => (
  <g>
    {/* stalk and leaf */}
    <path d="M60 41 C60 35 58 31 54 28" {...clayLine} strokeWidth={2.4} />
    <path
      d="M54 30 C48 22 38 22 32 27 C36 28 37 31 35 35 C40 34 43 36 44 40 C48 36 53 35 54 30 Z"
      fill={CLAY_LIGHT}
    />
    <path d="M53 30.5 C47 29 41 29 35.5 28.5 M46 30 C44 33 43 35 43.4 38" {...line} strokeWidth={1} />
    {GRAPES.map(([cx, cy]) => (
      <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={5.4} fill={CLAY} stroke={GLAZE} strokeWidth={1.3} />
    ))}
    {/* the three he kept */}
    <path d="M32 95 H88" {...clayLine} stroke={GILT} strokeWidth={1.2} />
    {[46, 60, 74].map((cx) => (
      <circle key={cx} cx={cx} cy={89.4} r={4.6} fill={GILT} stroke={GLAZE} strokeWidth={1.2} />
    ))}
  </g>
);

/**
 * Evening 4: Daria's wax tablet and the stylus. Two wooden leaves filled with
 * wax and tied along the long edge, so it opens upward across the knee. Drawn
 * that way on purpose: side by side, the leaves read as a bound book, and
 * there are no bound books in this courtyard.
 */
export const WaxTablet = () => (
  <g>
    <g transform="rotate(-5 60 56)">
      {[31, 57].map((y) => (
        <g key={y}>
          <rect x={33} y={y} width={54} height={23} rx={2.4} fill={CLAY} />
          <rect x={36.6} y={y + 3.6} width={46.8} height={15.8} rx={1.2} fill={GLAZE} />
          {[7.6, 11.5, 15.4].map((dy, i) => (
            <path
              key={dy}
              d={`M${40} ${y + dy} H${40 + (y === 31 ? [38, 30, 36] : [34, 38, 18])[i]}`}
              {...clayLine}
              stroke={CLAY_LIGHT}
              strokeWidth={1.1}
            />
          ))}
        </g>
      ))}
      {/* the cords that tie the two leaves */}
      <rect x={44} y={52.6} width={3.2} height={5.8} rx={1.2} fill={GILT} />
      <rect x={72.8} y={52.6} width={3.2} height={5.8} rx={1.2} fill={GILT} />
    </g>
    {/* stylus: a point to write with, a flat end to smooth the wax over */}
    <path d="M36 93.4 L83 88 L84.6 85.6 L88 85.4 L88.4 90.8 L85 90.9 L83.4 90.3 Z" fill={GILT} />
  </g>
);

/** Evening 5: the speech Philemon wrote that afternoon, and a sprig laid across it. */
export const Scroll = () => (
  <g>
    <rect x={40} y={36} width={40} height={48} fill={CLAY} />
    {/* the rolled ends, top and bottom */}
    {[31, 80].map((y) => (
      <g key={y}>
        <rect x={35} y={y} width={50} height={9} rx={4.5} fill={CLAY_LIGHT} />
        <path d={`M38 ${y + 4.5} H82`} {...line} strokeWidth={1} />
        <circle cx={33.4} cy={y + 4.5} r={2.6} fill={GILT} />
        <circle cx={86.6} cy={y + 4.5} r={2.6} fill={GILT} />
      </g>
    ))}
    {[46, 51.5, 57, 62.5, 68].map((y, i) => (
      <path key={y} d={`M45 ${y} H${45 + [30, 26, 30, 22, 14][i]}`} {...line} strokeWidth={1.2} />
    ))}
    {/* the sprig */}
    <path d="M47 77 C56 70 68 70 77 66" {...clayLine} stroke={GILT} strokeWidth={1.3} />
    {[
      [54, 72.4, -38], [61, 70.4, -24], [68, 69, -20], [74.4, 67, -22],
      [57, 73.6, 22], [64.4, 72, 28], [71, 70.4, 30],
    ].map(([cx, cy, r]) => (
      <ellipse key={`${cx}-${cy}`} cx={cx} cy={cy} rx={3.6} ry={1.5} fill={GILT} transform={`rotate(${r} ${cx} ${cy})`} />
    ))}
  </g>
);

/** Evening 6: a skiff at anchor, which is what Daria looked like standing still. */
export const Skiff = () => (
  <g>
    {/* mast, yard and the furled sail */}
    <path d="M60 64 V30" {...clayLine} strokeWidth={2.6} />
    <path d="M44 35 H76" {...clayLine} strokeWidth={2} />
    <path d="M45 38.4 C52 42 68 42 75 38.4 C68 37 52 37 45 38.4 Z" fill={CLAY_LIGHT} />
    <path d="M60 31 L45 62 M60 31 L75 62" {...clayLine} strokeWidth={0.9} />
    {/* hull, high at both ends */}
    <path
      d="M25 57 C30 58 33 61 35 64 H85 C87 61 90 58 95 55 C94 69 85 78 60 78 C37 78 27 69 25 57 Z"
      fill={CLAY}
    />
    <path d="M33 68 C46 72.5 74 72.5 88 67" {...line} />
    <circle cx={86.5} cy={62.5} r={1.9} fill={GLAZE} />
    <circle cx={86.5} cy={62.5} r={0.7} fill={CLAY_LIGHT} />
    {/* the anchor line */}
    <path d="M30 63 C27 72 25 80 24 86" {...clayLine} stroke={GILT} strokeWidth={1.1} />
    {/* water */}
    <path d="M24 86 q5 -6 10 0 t10 0 t10 0 t10 0 t10 0 t10 0 t10 0" {...clayLine} stroke={GILT} strokeWidth={1.8} />
    <path d="M32 93 q5 -5 10 0 t10 0 t10 0 t10 0 t10 0" {...clayLine} stroke={GILT} strokeWidth={1.4} />
  </g>
);

const jar = 'M53 28 H67 V32.6 H64.6 L64.6 43 C77 47 81 58 79 68 C77 80 67 89 62 95 H58 C53 89 43 80 41 68 C39 58 43 47 55.4 43 L55.4 32.6 H53 Z';

const Jar = ({ transform, fill = CLAY }) => (
  <g transform={transform}>
    <path d="M55.6 34.6 C43 34 41 44 47 49.6" {...clayLine} stroke={fill} strokeWidth={3} />
    <path d="M64.4 34.6 C77 34 79 44 73 49.6" {...clayLine} stroke={fill} strokeWidth={3} />
    <path d={jar} fill={fill} />
    <path d="M56 37.4 H64" {...line} strokeWidth={1.1} />
    <path d="M42.4 58 C53 61.6 67 61.6 77.6 58" {...line} />
    <path d="M41.4 66 C53 69.8 67 69.8 78.6 66" {...line} />
  </g>
);

/** Evening 7: Theron's first cargo on his own account. Forty jars, every one owed for. */
export const Jars = () => (
  <g>
    <Jar transform="translate(1 30) scale(0.56)" fill={CLAY_LIGHT} />
    <Jar transform="translate(52 30) scale(0.56)" fill={CLAY_LIGHT} />
    <Jar transform="translate(0 -3)" />
  </g>
);

// Bay leaves in pairs up both sides of a ring, open at the top, the way a
// garland is tied. Angles are measured from the foot of the ring.
const BAY = Array.from({ length: 9 }, (_, i) => 20 + i * 16.5);
const RING = 32;

const BayLeaf = ({ angle, side, lean }) => {
  const a = (angle * Math.PI) / 180;
  const cx = 60 + side * RING * Math.sin(a);
  const cy = 60 + RING * Math.cos(a);
  // Lying along the ring and pointing up it, then leant out of it or into it.
  const turn = side * (90 - angle + lean);
  return (
    <path
      d="M0 3 C-3.2 0 -3 -6.4 0 -10.4 C3 -6.4 3.2 0 0 3 Z"
      fill={lean > 0 ? CLAY : CLAY_LIGHT}
      transform={`translate(${cx.toFixed(2)} ${cy.toFixed(2)}) rotate(${turn.toFixed(1)})`}
    />
  );
};

/** Evening 8: the bay garland from the night before, and the cup that is yours. */
export const GarlandAndCup = () => (
  <g>
    {BAY.map((angle) => (
      <React.Fragment key={angle}>
        {[-1, 1].map((side) => (
          <React.Fragment key={side}>
            <BayLeaf angle={angle} side={side} lean={30} />
            <BayLeaf angle={angle} side={side} lean={-30} />
          </React.Fragment>
        ))}
      </React.Fragment>
    ))}
    {/* the tie at the foot of the garland */}
    <path d="M57 92 L52.6 99 M63 92 L67.4 99" {...clayLine} stroke={GILT} strokeWidth={1.6} />
    <circle cx={60} cy={92.4} r={2.8} fill={GILT} />
    <Cup x={60} y={60} scale={1.15} />
  </g>
);

export const EVENING_MOTIFS = {
  1: JugAndCup,
  2: LampAndMoth,
  3: Grapes,
  4: WaxTablet,
  5: Scroll,
  6: Skiff,
  7: Jars,
  8: GarlandAndCup,
};
